import { useEffect, useState } from 'react'
import { Spinner, Table } from '@heroui/react'
import { api } from '../lib/api'
import type { EstimateAccuracy, EstimateRow } from '../lib/estimate'
import { latestRead } from '../lib/latestRead'
import { explain } from '../lib/staffErrors'
import type { Branch } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { liveShare, queueLabel, signedDuration } from './estimatesCalc'
import { duration, figuresSheet, formatValue, group, isoToday, periodRange, type PeriodId } from './insightCalc'
import { download, xlsx } from './xlsx'
import { PeriodMenu } from './ReportBar'
import { NoData, Tile } from './insightVisuals'
import { Notice, Pick } from './kit'

/**
 * How close the waits customers were told came to the waits they had, for one
 * branch: per business day and queue, and in total (GET /report/estimates).
 */
export function Estimates({ branches }: { branches: Branch[] }) {
  const { lang, say } = useStaffText()
  const today = isoToday()
  const [branchId, setBranchId] = useState('')
  const [period, setPeriod] = useState<PeriodId>('week')
  const [custom, setCustom] = useState<[string, string]>([today, today])
  const [report, setReport] = useState<EstimateAccuracy | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const shown = branchId || branches[0]?.id || ''
  const [from, to] = periodRange(period, today, custom)
  const badDates = period === 'custom' && (!from || !to || from > to)

  useEffect(() => {
    if (!shown || badDates) return setBusy(false)
    setBusy(true)
    setError('')
    return latestRead((signal) => api.estimateAccuracy(shown, from, to, signal), {
      done: setReport,
      failed: (err) => {
        setError(explain(err, lang, 'common.unreachable'))
        setReport(null)
      },
      settled: () => setBusy(false),
    })
  }, [shown, from, to, badDates]) // eslint-disable-line react-hooks/exhaustive-deps

  const pct = (share: number | null) => formatValue(share, 'pct', lang)
  const cells = (r: EstimateRow) => [
    group(r.count),
    duration(r.medianErrorSec, lang),
    pct(r.count ? r.withinFiveMinPct / 100 : null),
    signedDuration(r.meanErrorSec, lang),
    group(r.under),
    group(r.over),
    pct(liveShare(r)),
  ]
  const heads = [
    say('estimate.report.tickets'),
    say('estimate.report.median'),
    say('estimate.report.within'),
    say('estimate.report.mean'),
    say('estimate.report.longer'),
    say('estimate.report.shorter'),
    say('estimate.report.live'),
  ]

  const exportTable = () => {
    if (!report) return
    const branch = branches.find((b) => b.id === shown)?.name ?? ''
    const title = say('pivot.bi.estimates')
    const values = (r: EstimateRow): (number | null)[] => [
      r.count,
      r.medianErrorSec,
      r.count ? r.withinFiveMinPct / 100 : null,
      r.meanErrorSec,
      r.under,
      r.over,
      liveShare(r),
    ]
    const rows: [string, ...(number | null)[]][] = [
      ...report.rows.map((r): [string, ...(number | null)[]] => [`${r.businessDate} ${queueLabel(r)}`, ...values(r)]),
      [say('insight.total'), ...values(report.total)],
    ]
    const sheet = figuresSheet(title, `${branch}; ${report.range.from} – ${report.range.to}`, ['', ...heads], rows, ['int', 'dur', 'pct', 'dur', 'int', 'int', 'pct'], say('insight.minutes'))
    download(xlsx(sheet), `${branch} - ${title} - ${report.range.from} ${report.range.to}`.replace(/[\\/:*?"<>|]/g, ' ') + '.xlsx')
  }

  return (
    <div className="insight">
      <div className="insight-top">
        <div className="insight-top__row">
          <Pick label={say('common.branch')} caption={say('common.branch')} value={shown} onChange={setBranchId} options={branches.map((b) => ({ value: b.id, label: b.name }))} />
          <PeriodMenu period={period} onPeriod={setPeriod} custom={custom} onCustom={setCustom} today={today} bad={badDates} />
          <span className="insight-spacer" />
          {busy && (
            <span className="insight-busy">
              <Spinner size="sm" />
              {say('common.reading')}
            </span>
          )}
        </div>
      </div>

      <div className="insight-body">
        <main className="insight-main">
          {badDates ? (
            <p className="insight-problem" role="alert">
              {say('insight.badDates')}
            </p>
          ) : error ? (
            <Notice>{error}</Notice>
          ) : (
            report &&
            (report.total.count === 0 ? (
              <p className="insight-nodata">{say('estimate.report.none')}</p>
            ) : (
              <>
                <div className="insight-kpis insight-kpis--5">
                  {heads.slice(0, 4).map((head, i) => (
                    <div key={head} className="insight-kpi">
                      <span className="insight-kpi__label">{head}</span>
                      <strong className="insight-kpi__value">{cells(report.total)[i]}</strong>
                    </div>
                  ))}
                  <div className="insight-kpi">
                    <span className="insight-kpi__label">{say('estimate.report.live')}</span>
                    <strong className="insight-kpi__value">{pct(liveShare(report.total))}</strong>
                  </div>
                </div>

                <div className="insight-canvas">
                  <Tile id="estrows" title={say('estimate.report.byDay')} focused={false} onBack={() => {}} menu={[{ label: say('insight.exportExcel'), run: exportTable }]}>
                    {report.rows.length === 0 ? (
                      <NoData />
                    ) : (
                      <Table variant="secondary" className="insight-grid">
                        <Table.ScrollContainer>
                          <Table.Content aria-label={say('estimate.report.byDay')}>
                            <Table.Header>
                              <Table.Column id="day" isRowHeader>
                                {say('estimate.report.day')}
                              </Table.Column>
                              <Table.Column id="queue">{say('estimate.report.queue')}</Table.Column>
                              {heads.map((h) => (
                                <Table.Column key={h} id={h} className="insight-grid__num">
                                  {h}
                                </Table.Column>
                              ))}
                            </Table.Header>
                            <Table.Body>
                              {report.rows.map((r) => (
                                <Table.Row key={`${r.businessDate}|${r.queueId}`} id={`${r.businessDate}|${r.queueId}`}>
                                  <Table.Cell>{r.businessDate}</Table.Cell>
                                  <Table.Cell>{queueLabel(r)}</Table.Cell>
                                  {cells(r).map((c, i) => (
                                    <Table.Cell key={i} className="insight-grid__num insight-num">
                                      {c}
                                    </Table.Cell>
                                  ))}
                                </Table.Row>
                              ))}
                              <Table.Row id="__total" className="insight-total">
                                <Table.Cell colSpan={2}>{say('insight.total')}</Table.Cell>
                                {cells(report.total).map((c, i) => (
                                  <Table.Cell key={i} className="insight-grid__num insight-num">
                                    {c}
                                  </Table.Cell>
                                ))}
                              </Table.Row>
                            </Table.Body>
                          </Table.Content>
                        </Table.ScrollContainer>
                      </Table>
                    )}
                  </Tile>
                </div>
              </>
            ))
          )}
        </main>
      </div>
    </div>
  )
}
